import { useState } from "react";
import { positions, useAlert } from "react-alert";
import { Button, Spinner } from "react-bootstrap";
import Cookies from "js-cookie";
import regenerateToken from "../regenerateToken";
import CoverPageTemplates from "./CoverPageTemplates";
import { FaImage, FaCloudUploadAlt, FaEye, FaSave, FaList } from "react-icons/fa";
import axios from 'axios';
import { buildRequestConfig } from '../../utils/apiHelper';
import { isAdmin as checkAdmin } from '../../utils/authUtils';

const CoverPageConfig = ({
  coverpageMap,
  retrieveExistingStateConfiguration,
}) => {
  const alert = useAlert();
  const [coverPageLabel, setCoverPageLabel] = useState("default");
  const [coverpageBase64, setCoverpageBase64] = useState("");

  const [loader, setLoader] = useState(false);
  // const [getLoader, setGetLoader] = useState(false); // Unused
  const [myFile, setMyFile] = useState("");
  // const [coverPageUrl, setCoverPageUrl] = useState(""); // Unused
  // const [searchCoverPage, setSearchCoverPage] = useState(""); // Unused
  const [showTemplate, setShowTemplate] = useState(false);
  const [selectedType, setSelectedType] = useState("template");
  const [fileExt, setFileExt] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const handleClose = () => setShowTemplate(false);
  const handleShow = () => setShowTemplate(true);

  const convertBase64 = (file) => {
    if (file) {
      switch (file.type) {
        case "image/svg+xml":
          setFileExt("svg");
          break;
        case "image/png":
          setFileExt("png");
          break;
        case "image/jpeg":
          setFileExt("jpg");
          break;
        default:
          setFileExt("jpg");
      }
    }
    if (file) {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        fileReader.onload = () => {
          resolve(fileReader.result);
        };
        fileReader.onerror = (error) => {
          reject(error);
        };
      });
    } else {
      return "";
    }
  };

  const validateImageType = (fileName) => {
    let result =
      fileName.endsWith(".jpg") ||
      fileName.endsWith(".jpeg") ||
      fileName.endsWith(".png") ||
      fileName.endsWith(".svg");
    return !result;
  };

  const handleCoverpageUpload = (event) => {
    let files = event.target.files;
    let fileSize = files[0]?.size;
    let fileSizeInMb = fileSize / (1024 * 1024);

    if (files?.length > 1) {
      alert.error(
        <span className="alertError">Please upload only 1 file.</span>,
        {
          position: positions.TOP_CENTER,
        }
      );

      return;
    }
    if (
      !files[0]?.type.startsWith("image/") ||
      validateImageType(files[0]?.name)
    ) {
      alert.error(
        <span className="alertError">
          Only image files having JPG, JPEG, PNG, and SVG formats are allowed!
        </span>,
        {
          position: positions.TOP_CENTER,
        }
      );

      return;
    }
    if (fileSize && fileSizeInMb > 1) {
      alert.error(
        <span className="alertError">File size should be less than 1 MB</span>,
        {
          position: positions.TOP_CENTER,
        }
      );
      return;
    }

    setMyFile(files[0]);
  };

  const saveBase64 = async (e) => {
    e.preventDefault();
    try {
      let fileBase64 = await convertBase64(myFile);
      if (fileBase64) setCoverpageBase64(fileBase64);
    } catch (err) {
      console.error("Base64 conversion failed", err);
      alert.error("Failed to process image file");
    }
  };

  const uploadCoverPage = async () => {
    let payload = {
      type: selectedType,
      fileType: fileExt,
      identifier: coverPageLabel,
      coverPageBase64: coverpageBase64,
    };

    const apiUrl =
      "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/assets/coverpage";
    // ── ADMIN PATH: use admin token (axios) + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({ method: "POST", url: apiUrl, data: payload }));
    }
    // ── CLIENT PATH (unchanged — uses native fetch) ────────────────────
    const idToken = Cookies.get("idToken");
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: idToken,
      },
      body: JSON.stringify(payload),
    };

    const response = await fetch(apiUrl, options);

    return response;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!coverPageLabel) {
      alert.error(
        <span className="alertError">Cover page label cannot be empty.</span>,
        {
          position: positions.TOP_CENTER,
        }
      );
      return;
    }
    setLoader(true);

    try {
      let response = await uploadCoverPage();

      if (response.status === 401) {
        await regenerateToken();
        response = await uploadCoverPage();
        if (response.status === 401) {
          throw new Error("Authentication failed even after token refresh");
        }
      }

      // Validate response before parsing
      if (response.status >= 200 && response.status < 300) {
        const data = await response.json();

        if (data.isSuccess) {
          alert.success(<span>Cover page uploaded successfully!</span>, {
            position: positions.TOP_CENTER,
          });
        } else {
          throw new Error(data.message || "Failed to upload cover page");
        }
      } else {
        throw new Error(`Upload failed with status: ${response.status}`);
      }
    } catch (e) {
      console.error("Error uploading cover page:", e);
      alert.error(
        <span className="alertError">
          {e.message || "Something went wrong. Please try again."}
        </span>,
        {
          position: positions.TOP_CENTER,
        }
      );
    } finally {
      setCoverPageLabel("default");
      setLoader(false);
      setCoverpageBase64("");
      setFileExt("");
      setSelectedTemplate("");
      retrieveExistingStateConfiguration();
    }
  };

  return (
    <div className="am-section">
      <div className="am-section-header">
        <h4><FaImage style={{ color: '#0ea5e9' }} /> Cover Page Configuration</h4>
      </div>

      <div className="am-section-body">
        <div className="am-grid">
          {/* Left: Upload Form */}
          <div>
            <form onSubmit={handleSubmit}>
              <div className="am-form-group">
                <label className="am-label">Cover Page Label</label>
                <input
                  className="am-input"
                  type="text"
                  value={coverPageLabel}
                  required
                  onChange={(e) => setCoverPageLabel(e.target.value)}
                />
              </div>

              <div className="am-radio-group">
                <label className="am-radio-label">
                  <input
                    type="radio"
                    name="coverType"
                    checked={selectedType === "template"}
                    onChange={() => setSelectedType("template")}
                  />
                  Choose Template
                </label>
                <label className="am-radio-label">
                  <input
                    type="radio"
                    name="coverType"
                    checked={selectedType === "custom"}
                    onChange={() => setSelectedType("custom")}
                  />
                  Custom Upload
                </label>
              </div>

              {selectedType === "custom" ? (
                <div className="am-form-group">
                  <label className="am-label">Upload Cover Page Info</label>
                  <div className="am-file-upload" onClick={() => document.getElementById('cp-upload').click()}>
                    <div style={{ pointerEvents: 'none' }}>
                      <FaCloudUploadAlt size={32} color="#94a3b8" />
                      <p style={{ margin: '0.5rem 0 0', color: '#64748b' }}>
                        {myFile ? myFile.name : "Click to Select Image"}
                      </p>
                      <p className="am-helper-text">JPG, PNG, SVG (Max 1MB)</p>
                    </div>
                  </div>
                  <input
                    id="cp-upload"
                    type="file"
                    accept="image/*"
                    onChange={(event) => handleCoverpageUpload(event)}
                    style={{ display: 'none' }}
                  />
                </div>
              ) : (
                <div className="mb-4">
                  {selectedTemplate && coverpageBase64 && (
                    <div className="am-template-box">
                      <strong>Chosen Template:</strong> {selectedTemplate}
                    </div>
                  )}

                  <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
                    <button
                      className="am-btn am-btn-primary"
                      onClick={(e) => {
                        e.preventDefault();
                        handleShow();
                      }}
                      style={{ background: '#f8fafc', color: '#334155', border: '1px solid #cbd5e1', boxShadow: 'none' }}
                    >
                      <FaList /> View Templates
                    </button>
                    <Button
                      className="am-btn am-btn-primary"
                      onClick={handleSubmit}
                      disabled={!coverpageBase64}
                    >
                      {loader ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <><FaCloudUploadAlt /> Upload Selected</>
                      )}
                    </Button>
                  </div>

                  {/* Modal Component */}
                  <CoverPageTemplates
                    showTemplate={showTemplate}
                    handleClose={handleClose}
                    setCoverpageBase64={setCoverpageBase64}
                    handleSubmit={handleSubmit}
                    saveBase64={saveBase64}
                    coverpageBase64={coverpageBase64}
                    setSelectedTemplate={setSelectedTemplate}
                    setFileExt={setFileExt}
                  />
                </div>
              )}

              {selectedType === "custom" && (
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                  <button
                    className="am-btn am-btn-primary"
                    onClick={saveBase64}
                    disabled={!myFile}
                    type="button"
                  >
                    <FaSave /> Save
                  </button>
                  <button
                    className="am-btn am-btn-primary"
                    type="submit"
                    disabled={!coverpageBase64}
                  >
                    {loader ? <Spinner animation="border" size="sm" /> : <><FaCloudUploadAlt /> Upload</>}
                  </button>
                </div>
              )}
            </form>
          </div>

          {/* Right: Existing List */}
          <div>
            <h5 className="am-label" style={{ fontSize: '1rem', marginBottom: '1rem' }}>Existing Cover Pages Library</h5>
            <div className="am-list-container">
              <div className="am-list-header">
                <div>SN</div>
                <div>Identifier Label</div>
                <div style={{ textAlign: 'center' }}>Link</div>
              </div>
              <div className="am-list-body">
                {typeof coverpageMap === "object" && !Object.keys(coverpageMap).length ? (
                  <div className="p-4 text-center text-muted">No cover pages found.</div>
                ) : typeof coverpageMap === "object" && Object.keys(coverpageMap).length ? (
                  Object.keys(coverpageMap).map((each, idx) => (
                    <div className="am-list-item" key={idx + 1}>
                      <div>{idx + 1}</div>
                      <div>{each}</div>
                      <div style={{ textAlign: 'center' }}>
                        <a
                          href={coverpageMap[each]}
                          target="_blank"
                          className="am-link"
                          title="View Image"
                          rel="noreferrer"
                        >
                          <FaEye /> View
                        </a>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-muted">No data found.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoverPageConfig;
